import React, {
  useContext, useEffect, useRef, useState,
} from 'react';
import { navigate } from 'gatsby-link';
import {
  CartService, CommerceService, OrderDetailsService, PageStateContext,
} from '../../../services';
import {
  defaultErrorMessage,
  formResponseEventName,
  getCheckoutSteps,
  successMessage,
} from './constants';
import { useCart } from '../cart/hooks/use-cart';
import { useOrderDetails } from './hooks/use-order-details';
import { useProducts } from '../cart/hooks/use-products';
import { CartRow } from '../cart/cart-row';
import { useInvoice } from '../cart/hooks/use-invoice';
import { useLimit } from '../cart/hooks/use-limit';
import { AdobeTracking } from '../../../tracking';

const getCountryLabel = (countryValue: string) => (
  countryValue === 'DE'
    ? 'Deutschland'
    : '—'
);

export const PlaceOrderForm = (): JSX.Element => {
  const { cartLink } = useContext(PageStateContext);
  const [error, setError] = useState(defaultErrorMessage);
  const formElement = useRef(null);
  const { cart, cartError, isFetchingCart } = useCart();
  const { products, isFetchingProducts } = useProducts(cart);
  const priceAndQuantities = products
    .map((product, index) => (
      {
        price: product.content?.price,
        quantity: cart[index]?.quantity || 0,
      }
    ));
  const isOverLimit = useLimit(priceAndQuantities);
  const invoice = useInvoice(priceAndQuantities);
  const { orderDetails, orderDetailsError, isFetchingOrderDetails } = useOrderDetails();
  const [isOrderPlaced, setIsOrderPlaced] = useState(false);
  const isLoading = (
    isFetchingCart
    || isFetchingOrderDetails
    || isFetchingProducts
    || products.length <= 0
    || Object.keys(orderDetails).length <= 0
  )
    && !isOrderPlaced;

  useEffect(() => {
    if (orderDetailsError || !formElement.current) {
      return;
    }
    formElement.current.injectData?.(orderDetails.data);
  }, [formElement.current, isLoading]);

  useEffect(() => {
    const handleFormResponse = async (event: CustomEvent) => {
      if (event.detail.status < 400) {
        const updatedData = {
          currency: 'EUR',
          items: cart.map((item) => ({
            coupon: '',
            currency: 'EUR',
            discount: 0,
            item_brand: 'Roche',
            item_category: 'Product',
            item_variant: '',
            item_id: item.productMetadataPageUniqueId,
            item_name: item.name,
            price: item.price,
            quantity: item.quantity,
          })),
          tax: invoice.taxPercent,
          shipping: invoice.shipping,
        };

        AdobeTracking(updatedData, 'purchase');
        CartService.clearCart({ preventUpdate: true });
        OrderDetailsService.clearOrderDetails({ preventUpdate: true });
        setIsOrderPlaced(true);
        return;
      }
      const parsedError = event.detail.json
        ? await event.detail.json().then((r: { message: string }) => r.message)
        : event.detail.message;

      setError(`${parsedError} ${defaultErrorMessage}`);
    };
    window.addEventListener(formResponseEventName, handleFormResponse);

    return () => {
      window.removeEventListener(formResponseEventName, handleFormResponse);
    };
  }, [cart]);

  useEffect(() => {
    const isInvalidCheckoutAttempt = (cart.length <= 0 && !isFetchingCart)
      || (Object.keys(orderDetails).length <= 0 && !isFetchingOrderDetails)
      || isOverLimit;

    if (cartError || orderDetailsError || isInvalidCheckoutAttempt) {
      navigate(cartLink as string);
    }
  }, [isFetchingCart, isFetchingOrderDetails, isOverLimit]);

  if (isLoading) {
    return <roche-loading-spinner />;
  }

  const {
    'first-name': firstName,
    'last-name': lastName,
    telephone,
    email,
    company,
    department,
    'account-ean': ean,
    'customer-po': po,
    notes,
    'shipping-address-street': shipStreet,
    'shipping-address-door': shipDoor,
    'shipping-address-zip-code': shipZip,
    'shipping-address-city': shipCity,
    'shipping-address-state': shipState,
    'shipping-address-country': shipCountry,
    'billing-address-same-as-shipping': billingSameAsShipping,
    'billing-address-street': billStreet,
    'billing-address-door': billDoor,
    'billing-address-zip-code': billZip,
    'billing-address-city': billCity,
    'billing-address-state': billState,
    'billing-address-country': billCountry,
  } = orderDetails.data;

  return (
    <roche-grid layout="8-4">
      <roche-journey
        slot="left"
        current-step={2}
        steps={JSON.stringify(getCheckoutSteps(cartLink as string))}
      />

      <roche-headline
        slot="left"
        level={3}
        headline="Einkaufswagen"
      />

      {products.map((product, index) => (
        <CartRow
          key={index}
          slot="left"
          disableUpdates={true}
          {...product}
          {...cart[index]}
        />))}

      <roche-invoice
        slot="left"
        {...{ ...invoice, taxPercent: `${invoice.taxPercent}` }}
        sum-label="Zwischensumme"
        tax-label="USt "
        tax-percent={`(${invoice.taxPercent}%)`}
        shipping-label="Versandkosten"
        shipping-tax={invoice.shippingTax}
        total-label="Gesamtbetrag"
      />

      <roche-grid layout="12" slot="left" class="no-padding-top">
        <roche-button-global
          text="Einkaufswagen ändern"
          type="secondary"
          appearance="secondary"
          href={cartLink as string}
          left-icon='arrow-left'
        />
      </roche-grid>

      <roche-headline
        slot="left"
        level={3}
        headline="Persönliche Daten"
      />
      <roche-grid layout="6-6" slot="left">
        <roche-richtext slot="left">
          <p><b>Nachname</b><br />{lastName}</p>
          <p><b>Vorname</b><br />{firstName}</p>
          <p><b>E-Mail Adresse</b><br />{email}</p>
        </roche-richtext>
        <roche-richtext slot="right">
          <p><b>Telefonnummer</b><br />{telephone}</p>
        </roche-richtext>
        <roche-richtext slot="left">
          <p><b>Firma</b><br />{company}</p>
          <p><b>Abteilung</b><br />{department}</p>
        </roche-richtext>
        <roche-richtext slot="right">
          {ean && <p><b>Kundennummer/EAN</b><br />{ean}</p>}
          {po && <p><b>PO-Nummer</b><br />{po}</p>}
        </roche-richtext>
      </roche-grid>

      <roche-grid layout="12" slot="left" class="no-padding-top">
        <roche-button-global
          text="Persönliche Daten ändern"
          type="secondary"
          appearance="secondary"
          href="/order-details"
          left-icon='arrow-left'
        />
      </roche-grid>

      <roche-grid layout="6-6" slot="left">

        <roche-headline
          slot="left"
          level={3}
          headline="Versandadresse"
        />
        <roche-richtext slot="left">
          <p>
            {shipStreet}, {shipDoor}<br />
            {shipZip} {shipCity}, {shipState}<br />
            {getCountryLabel(shipCountry)}
          </p>
        </roche-richtext>

        <roche-headline
          slot="right"
          level={3}
          headline="Rechnungsadresse"
        />
        <roche-richtext slot="right">
          {
            billingSameAsShipping
              ? (<p>
                {shipStreet}, {shipDoor}<br />
                {shipZip} {shipCity}, {shipState}<br />
                {getCountryLabel(shipCountry)}
              </p>)
              : (<p>
                {billStreet}, {billDoor}<br />
                {billZip} {billCity}, {billState}<br />
                {getCountryLabel(billCountry)}
              </p>)
          }
        </roche-richtext>

      </roche-grid>

      {notes && (
        <roche-grid layout="8-4" slot="left">
          <roche-headline
            slot="left"
            level={4}
            headline="Anmerkungen"
          />
          <roche-richtext slot="left">
            <p><em>{notes}</em></p>
          </roche-richtext>
        </roche-grid>
      )}

      <roche-form
        ref={formElement}
        slot="left"
        action={CommerceService.endpoints.placeOrder}
        submit="Jetzt kaufen"
        success={successMessage}
        error={error}
        disabled={isOrderPlaced}
      >
        <roche-input
          type="text"
          name="cart"
          required
          hidden
          value={JSON.stringify(cart)}
        />

        <roche-richtext type="footer" style={{ marginBottom: '-16px' }}>
          <p>
            Durch das Klicken der nachfolgenden Checkboxen erklären Sie,
            dass Sie die Vertragsbedingungen gelesen haben
            und mit ihnen einverstanden sind. Wir bitten Sie daher,
            die Vertragsbedingungen sorgfältig durchzulesen, bevor Sie diese akzeptieren.
          </p>
          <p>
            <a href="https://a.storyblok.com/f/94122/x/a4e44bc40f/webshop-agb.pdf" target="_blank" rel="noreferrer">Webshop-AGB</a><br />
            <a href="https://assets.cwp.roche.com/f/94122/x/a217eb1071/agb-rdd_verkaufs-lieferbedingungen_d_01-12-2023.pdf" target="_blank"
              rel="noreferrer">Allgemeine Verkaufs- und Lieferbedingungen der Roche Diagnostics Deutschland GmbH.</a><br />
          </p>
          <br />
        </roche-richtext>

        <roche-select
          style={{ marginTop: '16px' }}
          multi-select
          name="consent-webshop-terms-and-conditions"
          label="Webshop-AGB"
          options="[{&quot;label&quot;:&quot;Ich stimme den vorrangig geltenden Webshop AGB zu.&quot;,&quot;value&quot;:&quot;true&quot;}]"
          required
        />

        <roche-select
          style={{ marginTop: '16px' }}
          multi-select
          name="consent-terms-and-conditions"
          label="Allgemeine Verkaufs- und Lieferbedingungen der Roche Diagnostics Deutschland GmbH."
          options="[{&quot;label&quot;:&quot;Ich stimme den Allgemeinen Verkaufs- und Lieferbedingungen zu. Diese gelten nachrangig nach den Webshop AGB.&quot;,&quot;value&quot;:&quot;true&quot;}]"
          required
        />

        <roche-richtext type="footer" style={{ marginTop: '32px' }}>
          <p>
            Informationen zur Verarbeitung Ihrer Daten im Zusammenhang mit der Bestellung finden Sie in unserer Datenschutzerklärung unter www.roche.de/datenschutz. <a href='https://www.roche.de/datenschutz#95d9226e-3240-450c-87d9-8baae07cdf39' target='_blank' rel="noreferrer" style={{ whiteSpace: 'nowrap' }}>www.roche.de/datenschutz</a>
          </p>
          <p>
            Bitte beachten Sie, dass sich unser Angebot nur an Unternehmen richtet, die zu den Fachkreisen im Sinne von <a href='https://www.gesetze-im-internet.de/heilmwerbg/__2.html' target='_blank' rel="noreferrer" style={{ whiteSpace: 'nowrap' }}>§ 2 Heilmittelwerbegesetz</a> gehören und ihren Geschäftssitz in Deutschland haben. Wir benötigen daher von Ihnen die folgende Bestätigung.
          </p>
        </roche-richtext>

        <roche-select
          style={{ marginTop: '16px' }}
          multi-select
          label="Konfirmation"
          name="is-business-customer"
          options="[{&quot;label&quot;:&quot;Ich bestätige hiermit, dass unser Unternehmen zu den Fachkreisen im Sinne von § 2 Heilmittelwerbegesetz gehört und seinen Geschäftssitz in Deutschland hat.&quot;,&quot;value&quot;:&quot;true&quot;}]"
          required
        />

        <roche-richtext type="footer" style={{ marginTop: '24px' }}>
          <p style={{ color: 'var(--color-text--primary)' }}><strong>
            Bitte beachten Sie, dass der Kaufvertrag erst mit der Versandbestätigung zustande kommt.
            Siehe hierzu auch unsere Allgemeinen Webshop-AGB.
            Die Lieferung erfolgt innerhalb von zwei Wochen nach der Bestellung.
          </strong></p>
        </roche-richtext>
      </roche-form>

    </roche-grid>
  );
};
